/**
 * Scheduling context, the TimeBlock value object (docs/domain/03-core-model.md §1).
 * Identity is its value: weekday + start + end. Weekday 0 = Monday … 6 = Sunday
 * (the schema convention). Times are minutes since midnight, always whole
 * minutes, half-open [start, end), touching blocks (09–10, 10–11) don't overlap.
 */

export interface TimeBlock {
  /** 0 = Monday … 6 = Sunday. */
  weekday: number;
  /** Minutes since midnight, 0–1439. */
  startMinutes: number;
  /** Minutes since midnight, start < end ≤ 1440. */
  endMinutes: number;
}

export const WEEKDAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

export function isWeekday(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= 6;
}

/**
 * Weekday of a local calendar date, in the schema convention (0 = Monday …
 * 6 = Sunday). Pure: the date is a plain YYYY-MM-DD label, so this is calendar
 * arithmetic with no timezone involved. Booking derives the trusted weekday of
 * a requested block server-side rather than believing the client.
 */
export function weekdayOf(date: string): number {
  const [y, m, d] = date.split("-").map(Number);
  // getUTCDay: 0 = Sunday … 6 = Saturday → shift so Monday = 0.
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
}

/** "09:00" / "09:00:00" → minutes since midnight; null when malformed. */
export function parseTimeOfDay(raw: string): number | null {
  const match = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(raw.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** Minutes since midnight → "09:00". Accepts 1440 for a midnight end ("24:00"). */
export function formatTimeOfDay(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
}

/** "09:00–10:00", the display form used across screens. */
export function formatBlockTimes(block: Pick<TimeBlock, "startMinutes" | "endMinutes">): string {
  return `${formatTimeOfDay(block.startMinutes)}–${formatTimeOfDay(block.endMinutes)}`;
}

/** Value equality, a TimeBlock's identity (03-core-model.md §1). */
export function sameBlock(a: TimeBlock, b: TimeBlock): boolean {
  return (
    a.weekday === b.weekday &&
    a.startMinutes === b.startMinutes &&
    a.endMinutes === b.endMinutes
  );
}

/** Half-open overlap within one day. */
export function blocksOverlap(a: TimeBlock, b: TimeBlock): boolean {
  return (
    a.weekday === b.weekday &&
    a.startMinutes < b.endMinutes &&
    b.startMinutes < a.endMinutes
  );
}

/** Stable ordering: weekday, then start time. */
export function compareBlocks(a: TimeBlock, b: TimeBlock): number {
  return a.weekday - b.weekday || a.startMinutes - b.startMinutes;
}
